import { useCallback, useEffect, useRef } from "react";

export interface IntersectionObserverOptions extends IntersectionObserverInit {
  unobserveOnIntersection?: boolean;
}

const useIntersectionObserver = (
  element: React.RefObject<Element | null>,
  callback: (isVisible: boolean) => void,
  options: IntersectionObserverOptions = {}
): void => {
  const intersectionObserver = useRef<ResizeObserver>();

  const unobserve = useCallback(
    () => intersectionObserver.current?.disconnect(),
    []
  );

  const intersectionObserverCallback: IntersectionObserverCallback =
    useCallback(
      (entries) => {
        const { isIntersecting } = entries[0];

        callback(isIntersecting);

        if (isIntersecting && options.unobserveOnIntersection) {
          unobserve();
        }
      },
      [callback, options.unobserveOnIntersection, unobserve]
    );

  useEffect(() => {
    if (!element.current) {
      return undefined;
    }

    intersectionObserver.current = new IntersectionObserver(
      intersectionObserverCallback,
      options
    );

    intersectionObserver.current.observe(element.current);

    return unobserve;
  }, [element, intersectionObserverCallback, options, unobserve]);
};

export default useIntersectionObserver;
